/**
 * WorkOrdersPanel — Mode INFO-ONLY (Batch #14.210).
 *
 * La gestion complète des travaux se fait désormais dans la fiche Immeuble.
 * Ce panneau sert uniquement de boîte de réception : afficher la liste
 * des work orders ouverts/récents + un lien vers la fiche Immeuble correspondante.
 *
 * Aucun bouton d'action (CSV/PDF/édition/factures) ici — uniquement consultation.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import {
  Loader2, RefreshCw, Wrench, ExternalLink, Info,
} from "lucide-react";
import { API, authH } from "./_shared";
import ArchiveButton from "@/components/admin/ArchiveButton";
import AdminTaskStatusSelector from "@/components/admin/AdminTaskStatusSelector";

const STATUS_LABELS = {
  open: "Ouvert",
  scheduled: "Planifié",
  in_progress: "En cours",
  done: "Terminé",
  cancelled: "Annulé",
};

const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700 border-blue-200",
  scheduled: "bg-violet-100 text-violet-700 border-violet-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function WorkOrdersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/work-orders`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4" data-testid="admin-wo-panel">
      {/* Header simple */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-800 inline-flex items-center gap-2">
          <Wrench className="w-5 h-5 text-amber-600" /> Suivi des travaux
        </h2>
        <button onClick={load} className="text-xs px-2 py-1.5 rounded border border-slate-200 bg-white inline-flex items-center gap-1" data-testid="admin-wo-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* Bannière info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="admin-wo-info-banner">
        <Info className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-amber-900">
          <b>Boîte de réception uniquement.</b> Pour ajouter une facture, modifier le statut ou gérer les détails d'un travail,
          ouvrez la fiche de l'immeuble correspondant.
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-slate-400">
          Aucun travail à afficher
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {items.map((w) => (
            <div
              key={w.id}
              className="bg-white border border-slate-200 rounded-lg p-3 hover:border-amber-300 transition"
              data-testid={`admin-wo-item-${w.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-800">{w.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${STATUS_COLORS[w.status] || ""}`}>
                      {STATUS_LABELS[w.status] || w.status}
                    </span>
                    <span>{w.category}</span>
                    {w.contractor_name && <span>· 👷 {w.contractor_name}</span>}
                    {(w.invoices_total > 0) && (
                      <span className="text-emerald-700 font-bold">
                        · {(w.invoices_total || 0).toFixed(2)} $
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <AdminTaskStatusSelector
                    itemType="work_order"
                    itemId={w.id}
                    value={w.admin_task_status}
                    onChanged={load}
                  />
                  {w.master_id && (
                    <Link
                      to={`/admin/immeubles/${w.master_id}/suivi-travaux`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-[11px] font-medium hover:bg-blue-100"
                      title="Ouvrir la fiche Immeuble pour gérer ce travail"
                      data-testid={`admin-wo-open-immeuble-${w.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Immeuble
                    </Link>
                  )}
                  <ArchiveButton
                    itemType="work_order"
                    itemId={w.id}
                    itemLabel="ce travail"
                    onArchived={load}
                    size="xs"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
